import React, { useCallback, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ConflictErrorData,
  fetchWithAuth,
  FormErrorData,
  MagicLinkResponse,
  API_DOMAIN,
} from "../../../api-client/APIClient";
import { useMergeLink } from "@mergeapi/react-merge-link";
import { fetchOrganizationIntegrationSettings } from "../../../api-client/organization/OrganizationIntegrationSettingsAPIClient";
import { showErrorToast } from "../../shared/Toasts";
import {
  Button,
  ButtonVariant,
  Checkbox,
  CopyTextField,
  Dialog,
  Link,
  Text,
  TextField,
  Tooltip,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import { getIntegrationCategoryList, categoryDisplayName } from "../../../models/Helpers";
import { ExternalLink, Sparkles } from "lucide-react";
import useAppContext from "../../context/useAppContext";
import { useHistory } from "react-router-dom";
import {
  DOCS_MAGIC_LINK_PATH,
  MERGE_END_USER_ORIGIN_ID_HELP_CENTER,
  navigateToLinkedAccountDetailPageByID,
} from "../../../router/RouterUtils";
import * as Frigade from "@frigade/react";
import {
  APICategory,
  LinkedAccount,
  OrganizationIntegrationSettings,
  UnreleasedAPICategory,
  UserType,
} from "../../../models/Entities";

// Integrations that support configuration before opening the picker.
// Add new slugs here when extending (e.g., "microsoft-teams" for chat picker).
const INTEGRATIONS_WITH_PICKER_CONFIG = new Set(["sharepoint", "microsoft-teams"]);

interface IntegrationOption {
  value: string;
  label: string;
}

type IntegrationState = Record<string, IntegrationOption[]> | undefined;

interface AddProductionLinkedAccountButtonProps {
  isGetStarted?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  linkedAccount?: LinkedAccount;
  isFilePicker?: boolean;
  isChannelPicker?: boolean;
  isEmailPicker?: boolean;
}

const AddProductionLinkedAccountButton = ({
  isGetStarted,
  disabled = false,
  disabledReason,
  linkedAccount,
  isFilePicker,
  isChannelPicker,
  isEmailPicker,
}: AddProductionLinkedAccountButtonProps) => {
  const { user } = useAppContext();
  const isMemberRole = user.type === UserType.member;
  const isSupportRole = user.type === UserType.support;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [endUserOrganizationName, setEndUserOrganizationName] = useState<string | undefined>(
    linkedAccount?.end_user.origin_id ?? undefined,
  );
  const [url, setURL] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleSubmit, errors, control, reset } = useForm({
    mode: "onBlur",
    defaultValues: {
      uniqueIdentifier: linkedAccount?.end_user.origin_id ?? undefined,
      email: linkedAccount?.end_user.email_address ?? undefined,
      organizationName: linkedAccount?.end_user.organization_name ?? undefined,
      category: (linkedAccount?.category as APICategory) ?? undefined,
    },
  });
  const [selectedCategory, setSelectedCategory] = useState<APICategory | null>(null);

  const [disableOnboardingScreen, setDisableOnboardingScreen] = useState<boolean>(
    user.disable_magic_link_onboarding,
  );
  const [dismissOnboardingScreen, setDismissOnboardingScreen] = useState<boolean>(
    user.disable_magic_link_onboarding,
  );
  const [isConflictingCategoryAndEndUserOriginId, setIsConflictingCategoryAndEndUserOriginId] =
    useState<boolean>(false);
  const [linkedAccountID, setLinkedAccountID] = useState<string | undefined>(undefined);
  const [categoryToIntegrationMap, setCategoryToIntegrationsMap] =
    useState<IntegrationState>(undefined);

  const history = useHistory();

  // File picker state for embedded link
  const [filePickerWasSuccess, setFilePickerWasSuccess] = useState(false);
  const [filePickerLinkToken, setFilePickerLinkToken] = useState<string>("");
  const [isFilePickerConfigOpen, setIsFilePickerConfigOpen] = useState(false);
  const [enableSensitivityLabels, setEnableSensitivityLabels] = useState(false);

  // useMergeLink hook for embedded file picker
  const { open: openFilePicker, isReady: isFilePickerReady } = useMergeLink({
    linkToken: filePickerLinkToken,
    onSuccess: () => {
      setFilePickerWasSuccess(true);
    },
    onExit: () => {
      if (filePickerWasSuccess) {
        setIsModalOpen(false);
      }
    },
    tenantConfig: {
      apiBaseURL: API_DOMAIN,
    },
  });

  useEffect(() => {
    fetchOrganizationIntegrationSettings({
      onlyEnabled: true,
      onFetch: (data) => {
        const integrationMap: IntegrationState = {};

        data.forEach((organizationIntegration: OrganizationIntegrationSettings) => {
          const integration = organizationIntegration.integration;
          const categories = integration.categories;

          categories.forEach((category) => {
            if (!integrationMap[category]) {
              integrationMap[category] = [];
            }

            if (
              !integrationMap[category].some(
                (item) => item.value === integration.slug && item.label === integration.name,
              )
            ) {
              integrationMap[category].push({
                value: integration.slug,
                label: integration.name,
              });
            }
          });
        });

        setCategoryToIntegrationsMap(integrationMap);
      },
    });
  }, []);

  const onDisableMagicLinkOnboarding = useCallback(() => {
    setIsLoading(true);

    fetchWithAuth({
      path: `/users/me/disable-magic-link-onboarding`,
      method: "PATCH",
      onResponse: (data: any) => {
        if (!data?.success) {
          showErrorToast("An error has occurred. Please try again later.");
        } else {
          setDisableOnboardingScreen(data.success);
        }
        setIsLoading(false);
      },
      onError: (err: Response | undefined) => {
        if (err) {
          err.json().then((data: FormErrorData) => {
            let wasToastShown = false;
            for (const field_name in data) {
              if (field_name === "non_field_errors") {
                showErrorToast(data[field_name][0]);
                wasToastShown = true;
              }
            }
            if (!wasToastShown) {
              showErrorToast("An error has occurred. Please try again later.");
            }
          });
        } else {
          showErrorToast("A network error has occurred. Please try again.");
        }
        setIsLoading(false);
      },
    });
  }, []);

  const buildIntegrationSpecificConfig = (): Record<string, any> | undefined => {
    const slug = linkedAccount?.integration?.slug;
    if (slug === "sharepoint") {
      return { sharepoint: { enable_sensitivity_labels: enableSensitivityLabels } };
    }
    if (slug === "microsoft-teams") {
      return { microsoft_teams: { enable_sensitivity_labels: enableSensitivityLabels } };
    }
    return undefined;
  };

  const handleProductionFilePickerDirect = useCallback(
    (integrationSpecificConfig?: Record<string, any>) => {
      if (!linkedAccount) return;

      setIsLoading(true);
      const formData: Record<string, any> = {
        end_user_origin_id: linkedAccount.end_user.origin_id,
        end_user_organization_name: linkedAccount.end_user.organization_name,
        end_user_email_address: linkedAccount.end_user.email_address,
        categories: [linkedAccount.category],
        should_create_magic_link_url: false,
        completed_account_initial_screen: "SELECTIVE_SYNC",
        ...(integrationSpecificConfig && {
          integration_specific_config: integrationSpecificConfig,
        }),
      };

      fetchWithAuth({
        path: "/integrations/create-link-token",
        method: "POST",
        body: formData,
        onResponse: (data: any) => {
          if (!data?.link_token) {
            showErrorToast("An error has occurred. Please try again later.");
          } else {
            setFilePickerLinkToken(data.link_token);
            setTimeout(() => {
              if (isFilePickerReady) {
                setFilePickerWasSuccess(false);
                openFilePicker();
              }
            }, 100);
          }
          setIsLoading(false);
        },
        onError: (err: Response | undefined) => {
          if (err && err!.status == 403) {
            showErrorToast(
              "Insufficient permissions. Please contact your Merge administrator to access this feature.",
            );
          } else {
            showErrorToast("An error has occurred. Please try again later.");
          }
          setIsLoading(false);
        },
      });
    },
    [linkedAccount, isFilePickerReady, openFilePicker],
  );

  const onSubmit = useCallback(
    (data: {
      uniqueIdentifier: string;
      email: string;
      organizationName: string;
      category: string;
      integration: IntegrationOption | null;
    }) => {
      setIsLoading(true);
      const isEmbeddedPicker = !!(isFilePicker || isChannelPicker || isEmailPicker);
      const formData = {
        end_user_origin_id: data.uniqueIdentifier,
        end_user_organization_name: data.organizationName,
        end_user_email_address: data.email,
        categories: [data.category],
        should_create_magic_link_url: !isEmbeddedPicker,
        link_expiry_mins: 10080,
        ...(data.integration ? { integration: data.integration.value } : null),
        ...(isEmbeddedPicker ? { completed_account_initial_screen: "SELECTIVE_SYNC" } : {}),
      };

      fetchWithAuth({
        path: isEmbeddedPicker
          ? "/integrations/create-link-token"
          : linkedAccount
          ? `/integrations/create-link-token-for-magic-link?is_relink=True`
          : `/integrations/create-link-token-for-magic-link`,
        method: "POST",
        body: formData,
        onResponse: (data: any) => {
          if (isEmbeddedPicker) {
            // Production file/channel picker - use embedded link with link_token
            if (!data?.link_token) {
              showErrorToast("An error has occurred. Please try again later.");
            } else {
              // Use useMergeLink for embedded experience
              setFilePickerLinkToken(data.link_token);
              setTimeout(() => {
                if (isFilePickerReady) {
                  setFilePickerWasSuccess(false);
                  openFilePicker();
                }
              }, 100);
            }
          } else if (!data?.magic_link_url) {
            showErrorToast("An error has occurred. Please try again later.");
          } else {
            setEndUserOrganizationName(formData.end_user_organization_name);
            setURL(data.magic_link_url);
          }
          setIsLoading(false);
        },
        onError: (err: Response | undefined) => {
          if (err) {
            if (err!.status == 403) {
              showErrorToast(
                "Insufficient permissions. Please contact your Merge administrator to access this feature.",
              );
            } else if (err!.status == 409) {
              setIsConflictingCategoryAndEndUserOriginId(true);
              err.json().then((data: ConflictErrorData) => {
                if ("linked_account_id" in data) {
                  setLinkedAccountID(data.linked_account_id);
                }
              });
            } else {
              err.json().then((data: FormErrorData) => {
                let wasToastShown = false;
                if ("non_field_errors" in data) {
                  showErrorToast(data.non_field_errors[0]);
                  wasToastShown = true;
                }
                if (!wasToastShown) {
                  showErrorToast("An error has occurred. Please try again later.");
                }
              });
            }
          } else {
            showErrorToast("A network error has occurred. Please try again.");
          }
          setIsLoading(false);
        },
      });
    },
    [setIsLoading],
  );

  const magicLinkOnboarding = (
    <>
      <Text as="p">
        Merge’s Magic Link allows you to deliver an in-browser linking experience and create
        Production Linked Accounts without any frontend code.
      </Text>
      <Text as="p">
        Send your users a secure URL to authorize their integrations in production.
      </Text>
      <Checkbox
        className="my-6"
        label="Don't show this screen again"
        onChange={setDisableOnboardingScreen}
        checked={disableOnboardingScreen}
      />
      <div className="flex flex-row gap-x-4">
        <Button
          onClick={() => window.open(DOCS_MAGIC_LINK_PATH, "_blank")}
          rightIcon={<ExternalLink size={16} />}
          variant={ButtonVariant.SecondaryGray}
          fullWidth
        >
          Learn more
        </Button>
        <Button
          onClick={() => {
            if (disableOnboardingScreen) {
              onDisableMagicLinkOnboarding();
            }
            setDismissOnboardingScreen(true);
          }}
          variant={ButtonVariant.PrimaryGray}
          fullWidth
        >
          Next
        </Button>
      </div>
    </>
  );

  const conflictingUsers = (
    <>
      <Text as="p" variant="h6">
        Looks like the account {endUserOrganizationName} you are trying to connect already exists!
      </Text>
      <Text as="p">Visit the Linked Account’s page to generate a relink URL.</Text>
      <div className="flex flex-row gap-x-4 mt-6">
        <Button
          onClick={() => setIsConflictingCategoryAndEndUserOriginId(false)}
          variant={ButtonVariant.TextBlack}
          fullWidth
        >
          Go back
        </Button>
        <Button
          onClick={() => navigateToLinkedAccountDetailPageByID(history, linkedAccountID!)}
          fullWidth
        >
          Go to Linked Account
        </Button>
      </div>
    </>
  );

  const form = !url && (
    <form className="flex flex-col gap-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Text variant="h6" className="mb-2">
          Organization name
        </Text>
        <div className="mt-2">
          <Controller
            name="organizationName"
            control={control}
            rules={{ required: true, minLength: 1 }}
            render={({ name, onChange }) => (
              <TextField
                name={name}
                placeholder="Enter your user’s organization name"
                error={errors.organizationName ? true : false}
                errorText="Please enter a valid organization name"
                onChange={onChange}
                defaultValue={linkedAccount?.end_user.organization_name ?? undefined}
              />
            )}
          />
        </div>
      </div>
      <div>
        <Text variant="h6">Email address</Text>
        <Text variant="sm" className="text-gray-40 mb-2">
          For identification purposes — this will not cause any emails to be sent
        </Text>

        <div className="mt-2">
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              validate: (value) => {
                const input = document.createElement("input");

                input.type = "email";
                input.required = true;
                input.value = value;

                const result =
                  typeof input.checkValidity === "function"
                    ? input.checkValidity() || input.validationMessage
                    : /\S+@\S+\.\S+/.test(value);
                input.remove();
                return result;
              },
            }}
            render={({ name, onChange }) => (
              <TextField
                name={name}
                placeholder="Enter your user’s email address"
                error={errors.email ? true : false}
                errorText="Please enter a valid email"
                onChange={onChange}
                defaultValue={linkedAccount?.end_user.email_address ?? undefined}
              />
            )}
          />
        </div>
      </div>
      <div>
        <Text variant="h6" className="mb-2">
          Category
        </Text>

        <Controller
          name="category"
          control={control}
          rules={{
            required: true,
          }}
          render={({ value, onChange, ref }) => (
            <Typeahead
              ref={ref}
              error={!!errors.category}
              errorText="Please select a category"
              placeholder="Select category..."
              getOptionLabel={(category: APICategory) => categoryDisplayName(category) ?? ""}
              value={(linkedAccount?.category as APICategory) ?? undefined}
              disabled={!!linkedAccount}
              options={getIntegrationCategoryList(user)}
              onChange={(_: any, category: APICategory | null) => {
                onChange(category);
                setSelectedCategory(category);
              }}
            />
          )}
        />
      </div>
      {categoryToIntegrationMap && (
        <div>
          <Text variant="h6">Integration</Text>
          <Text variant="sm" className="text-gray-40">
            Optional. If set, creates a Magic Link URL for the specified integration
          </Text>
          <div className="mt-2">
            <Controller
              name="integration"
              control={control}
              render={({ value, onChange, ref }) => (
                <Typeahead
                  ref={ref}
                  placeholder="Select integration..."
                  disabled={!!linkedAccount || !selectedCategory}
                  options={
                    selectedCategory
                      ? (categoryToIntegrationMap[selectedCategory] ?? []).sort((a, b) =>
                          a.label.localeCompare(b.label),
                        )
                      : []
                  }
                  getOptionLabel={(option: IntegrationOption) => option.label}
                  onChange={(_: any, integration: IntegrationOption | null) => {
                    onChange(integration);
                  }}
                />
              )}
            />
          </div>
        </div>
      )}
      <div>
        <Text variant="h6">End user origin ID</Text>
        <Text variant="sm" className="text-gray-40">
          Uniquely identifies a Linked Account i.e. a specific customer of yours for a given
          integration.{" "}
          <Link href={MERGE_END_USER_ORIGIN_ID_HELP_CENTER} target="_blank">
            Learn more
          </Link>
          .
        </Text>
        <div className="mt-2">
          <Controller
            name="uniqueIdentifier"
            control={control}
            rules={{
              required: true,
            }}
            render={({ name, onChange }) => (
              <TextField
                name={name}
                placeholder="Enter a unique end user origin ID"
                error={errors.uniqueIdentifier ? true : false}
                errorText="Please enter a valid unique identifier"
                onChange={onChange}
                defaultValue={linkedAccount?.end_user.origin_id ?? undefined}
                disabled={linkedAccount != undefined}
              />
            )}
          />
        </div>
      </div>
      <div className="flex flex-row gap-x-4 mt-6">
        <Button fullWidth onClick={() => setIsModalOpen(false)} variant={ButtonVariant.TextBlack}>
          Cancel
        </Button>
        <Button loading={isLoading} fullWidth leftIcon={<Sparkles size={16} />} type="submit">
          Generate URL
        </Button>
      </div>
    </form>
  );

  const urlDisplay = url && (
    // Magic Link URL is a one-time auth token; the heading interpolates end-user org name; both masked at the section (ph-no-capture for PostHog, data-fr-ignore for the Frigade Assistant DOM snapshot)
    <div className="ph-no-capture" data-fr-ignore>
      <Text as="p">
        Send this URL to your user to authorize their integration. This URL will expire after 7
        days.
      </Text>
      <Text variant="h6" className="mt-6 mb-2">
        Magic Link URL for {endUserOrganizationName}
      </Text>
      <CopyTextField text={url} url />
    </div>
  );

  const modalContent = () => {
    if (isConflictingCategoryAndEndUserOriginId) {
      return conflictingUsers;
    } else if (!dismissOnboardingScreen) {
      return magicLinkOnboarding;
    } else {
      return !url ? form : urlDisplay;
    }
  };

  const { track } = Frigade.useUser();

  return (
    <>
      <Frigade.Tour
        className="create_production_linked_account"
        flowId="flow_2JDRLtX1AZ1jeQFl"
        side="left"
        align="after"
        dismissible={false}
        avoidCollisions={false}
        css={{
          transform: "translate(calc(var(--radix-popper-anchor-width) / 2 - 10px), 10px)",
          ".fr-dot-wrapper": {
            scale: "0.5",
            transform: "translate(20px, -20px)",
          },
        }}
      />

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        footerButtonsHidden
        title="Magic Link"
        variant="md"
      >
        {modalContent()}
      </Dialog>

      <Dialog
        open={isFilePickerConfigOpen}
        onClose={() => setIsFilePickerConfigOpen(false)}
        title={isChannelPicker ? "Channel picker configuration" : "File picker configuration"}
        variant="sm"
        primaryButtonText={isChannelPicker ? "Open channel picker" : "Open file picker"}
        primaryButtonVariant={ButtonVariant.PrimaryGray}
        primaryButtonLoading={isLoading}
        onPrimaryButtonClick={() => {
          setIsFilePickerConfigOpen(false);
          handleProductionFilePickerDirect(buildIntegrationSpecificConfig());
        }}
        secondaryButtonText="Cancel"
        secondaryButtonVariant={ButtonVariant.TextBlack}
        onSecondaryButtonClick={() => setIsFilePickerConfigOpen(false)}
      >
        {(linkedAccount?.integration?.slug === "sharepoint" ||
          linkedAccount?.integration?.slug === "microsoft-teams") && (
          <Checkbox
            checked={enableSensitivityLabels}
            onChange={setEnableSensitivityLabels}
            label={
              <div className="flex flex-col">
                <Text>Enable sensitivity labels</Text>
                <Text variant="sm" className="text-gray-40">
                  Enable Microsoft Purview sensitivity labels for this{" "}
                  {isChannelPicker ? "channel" : "file"} picker session
                </Text>
              </div>
            }
          />
        )}
      </Dialog>

      {linkedAccount &&
      !isFilePicker &&
      !isChannelPicker &&
      !isEmailPicker &&
      !user.is_merge_link_button_disabled ? (
        <Tooltip
          title={
            isSupportRole
              ? "Insufficient permissions. Please contact your Merge administrator to access this feature."
              : ""
          }
        >
          <span>
            <Button
              variant={ButtonVariant.TertiaryWhite}
              onClick={() => {
                setEndUserOrganizationName(undefined);
                setURL(undefined);
                reset();
                setIsModalOpen(true);
              }}
              fullWidth
              disabled={isMemberRole || isSupportRole}
            >
              Relink with Magic Link
            </Button>
          </span>
        </Tooltip>
      ) : linkedAccount && isFilePicker && linkedAccount.category === APICategory.filestorage ? (
        <Button
          variant={ButtonVariant.TertiaryWhite}
          onClick={() => {
            const slug = linkedAccount.integration?.slug;
            if (slug && INTEGRATIONS_WITH_PICKER_CONFIG.has(slug)) {
              setEnableSensitivityLabels(false);
              setIsFilePickerConfigOpen(true);
            } else {
              handleProductionFilePickerDirect();
            }
          }}
          fullWidth
          disabled={isMemberRole || isSupportRole || isLoading}
        >
          Open File Picker
        </Button>
      ) : linkedAccount && isChannelPicker && linkedAccount.category === APICategory.chat ? (
        <Button
          variant={ButtonVariant.TertiaryWhite}
          onClick={() => {
            const slug = linkedAccount.integration?.slug;
            if (slug && INTEGRATIONS_WITH_PICKER_CONFIG.has(slug)) {
              setEnableSensitivityLabels(false);
              setIsFilePickerConfigOpen(true);
            } else {
              handleProductionFilePickerDirect();
            }
          }}
          fullWidth
          disabled={isMemberRole || isSupportRole || isLoading}
        >
          Open Channel Picker
        </Button>
      ) : linkedAccount &&
        isEmailPicker &&
        linkedAccount.category === UnreleasedAPICategory.email ? (
        <Button
          variant={ButtonVariant.TertiaryWhite}
          onClick={() =>
            handleProductionFilePickerDirect({
              "microsoft-outlook-email": { enable_sensitivity_labels: true },
            })
          }
          fullWidth
          disabled={isMemberRole || isSupportRole || isLoading}
        >
          Open Email S2
        </Button>
      ) : isGetStarted ? (
        // Hide the button if merge link buttons are disabled (except for file picker)
        user.is_merge_link_button_disabled ? null : (
          <Button
            onClick={() => {
              setEndUserOrganizationName(undefined);
              setURL(undefined);
              reset();
              setIsModalOpen(true);
            }}
            fullWidth
          >
            Create Magic Link via URL
          </Button>
        )
      ) : // Hide the button if merge link buttons are disabled (except for file picker)
      user.is_merge_link_button_disabled ? null : (
        <Tooltip
          title={
            disabled && disabledReason
              ? disabledReason
              : isSupportRole
              ? "Insufficient permissions. Please contact your Merge administrator to access this feature."
              : ""
          }
        >
          <span>
            <Button
              onClick={() => {
                setEndUserOrganizationName(undefined);
                setURL(undefined);
                reset();
                setIsModalOpen(true);
                track("user-opened-merge-link");
              }}
              disabled={disabled || isSupportRole}
            >
              Create production Linked Account
            </Button>
          </span>
        </Tooltip>
      )}
    </>
  );
};

export default AddProductionLinkedAccountButton;
